/**
 * KPIs page - port from `1116/omega-club/dashboard/src/components/KPIs.jsx`.
 * Reads from `useTenantData(slug)`, grouped by `category`.
 *
 * Per CHAP-81 (Phase 1C #47).
 *
 * NB on parity: omega-club's KPIs.jsx has per-card `sub`, `positive`, and
 * `benchmark` fields that the chapter-build `TenantKpi` shape doesn't carry
 * yet. Schema-extension to add `subText`, `positive`, `benchmarkLabel`,
 * `benchmarkStatus` is queued as a follow-up - until then this page renders
 * the four shipped fields (label / valueString / category / tooltip) which
 * is enough for the 4 seeded omega-zeta KPIs and the cascade-unblocker
 * pattern.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { TierGuard } from '../components/RoleGuard.js';
import type { TenantKpi } from '../../shared/types.js';

interface CategorySpec {
  id: string;
  label: string;
  accent: string;       // Tailwind border-l-* class
  dot: string;          // Tailwind bg-* class for the leading dot
  subtitle?: string;
}

// Category styling matches omega-club's KPI categorization.
const CATEGORY_SPECS: CategorySpec[] = [
  { id: 'cost',        label: 'Project Costs',     accent: 'border-l-navy-dark', dot: 'bg-navy-dark' },
  { id: 'income',      label: 'Income & Cash Flow',accent: 'border-l-positive',  dot: 'bg-positive',  subtitle: 'Net operating income, revenue, cash flow' },
  { id: 'debt',        label: 'Debt & Leverage',   accent: 'border-l-negative',  dot: 'bg-negative',  subtitle: 'Mortgage burden, DSCR, LTV' },
  { id: 'growth',      label: 'Growth & Returns',  accent: 'border-l-orange',    dot: 'bg-orange',    subtitle: 'Property value appreciation, IRR' },
  { id: 'fundraising', label: 'Fundraising',       accent: 'border-l-graph-blue',dot: 'bg-graph-blue',subtitle: 'Capital campaign, donor capacity' },
];

export default function KPIs() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');

  const byCategory = useMemo<Record<string, TenantKpi[]>>(() => {
    const map: Record<string, TenantKpi[]> = {};
    for (const spec of CATEGORY_SPECS) map[spec.id] = [];
    if (!data) return map;
    const sorted = [...data.kpis].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const kpi of sorted) {
      if (map[kpi.category]) {
        map[kpi.category]!.push(kpi);
      } else {
        // Unknown category - group under 'cost' as fallback so they don't disappear
        map['cost']!.push(kpi);
      }
    }
    return map;
  }, [data]);

  if (isLoading) {
    return <TenantLoading />;
  }
  if (error) {
    return <TenantError message={String(error)} onRetry={() => refetch()} />;
  }
  if (!data) {
    return <PageState label="No data" />;
  }

  const populatedCategories = CATEGORY_SPECS.filter((spec) => byCategory[spec.id]!.length > 0);

  return (
    // CHAP-81: KPIs are financial data (project costs, debt, fundraising) —
    // cabinet-only, same TierGuard mechanism ExecutiveSummary + Voting use
    // for cabinet-gated content. This is the client-side affordance; the
    // server does not yet independently enforce a KPI-specific tier check
    // (tenant-config.ts returns kpis to any tenant member), so this guard
    // hides rather than secures until a server-side requireTier lands.
    <TierGuard
      tier="cabinet"
      fallback={
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-sm text-neutral min-w-0">
          KPIs are available to Cabinet members. Ask a Cabinet member if you need access.
        </div>
      }
    >
      <div className="space-y-4 sm:space-y-5 min-w-0">
        <div>
          <h2 className="text-navy text-xl sm:text-2xl font-bold">Key Performance Indicators</h2>
          <p className="text-neutral text-sm mt-1">
            {data.kpis.length} metrics across {populatedCategories.length} categories
          </p>
        </div>

        {populatedCategories.map((spec) => (
          <CategorySection
            key={spec.id}
            spec={spec}
            kpis={byCategory[spec.id]!}
          />
        ))}

        {populatedCategories.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            No KPIs seeded for this tenant. Seed script lives at <code className="font-mono">apps/web/src/server/db/seed/omega-zeta.ts</code>.
          </div>
        )}
      </div>
    </TierGuard>
  );
}

function CategorySection({ spec, kpis }: { spec: CategorySpec; kpis: TenantKpi[] }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${spec.accent} p-4 sm:p-5`}>
      <div className="flex items-center gap-2 mb-3 flex-wrap min-w-0">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${spec.dot}`} />
        <h3 className="text-navy font-semibold text-sm">{spec.label}</h3>
        {spec.subtitle && (
          <span className="text-neutral text-xs">{spec.subtitle}</span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 min-w-0">
        {kpis.map((kpi) => (
          <MetricCard key={kpi.id} kpi={kpi} categoryId={spec.id} />
        ))}
      </div>
    </div>
  );
}

function MetricCard({ kpi, categoryId }: { kpi: TenantKpi; categoryId: string }) {
  const isPositive = categoryId === 'income';
  return (
    <div className="min-w-0" title={kpi.tooltip ?? undefined}>
      <p className="text-neutral text-xs uppercase tracking-wider">{kpi.label}</p>
      <p className={`${isPositive ? 'text-positive' : 'text-navy'} text-lg sm:text-xl font-bold mt-0.5 truncate`}>
        {kpi.valueString}
      </p>
      {kpi.tooltip && (
        <p className="text-neutral text-[10px] mt-1 line-clamp-2 leading-tight">{kpi.tooltip}</p>
      )}
    </div>
  );
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
